import type { CanvasEntity } from '../entityTypes';
import type { GroupData } from '../entityTypes';
import type { CanvasGroup } from '../types';
import type { GlobalTasksById } from '../../stores/types';
import { buildValidEntityIds } from './buildValidEntityIds';

export interface GroupDragPayload {
  entityIds: string[];
  collapsedGroupIds: string[];
  expandedGroupIds: string[];
}

/**
 * Given a group being dragged (+ optionally selected sibling groups),
 * compute the full set of entity IDs, collapsed group IDs, and expanded group IDs
 * that need to move together.
 */
export function resolveGroupDragPayload(
  groupId: string,
  selectedEntityIds: string[],
  rawEntities: CanvasEntity[],
  tasksById: GlobalTasksById,
): GroupDragPayload {
  const validEntityIds = buildValidEntityIds(rawEntities, tasksById);

  const rawGroups = rawEntities.filter(ent => ent.kind === 'group');
  const groupIdSet = new Set(rawGroups.map(g => g.id));
  const allValidIds = new Set([...validEntityIds, ...groupIdSet]);

  const groups: CanvasGroup[] = rawGroups.map(ent => {
    const gd = ent.data as unknown as GroupData;
    const memberNodeIds: string[] = [];
    const memberGroupIds: string[] = [];
    for (const mid of gd.memberEntityIds) {
      if (groupIdSet.has(mid)) memberGroupIds.push(mid);
      else memberNodeIds.push(mid);
    }
    return {
      id: ent.id,
      label: gd.label,
      description: gd.description,
      backgroundColor: gd.backgroundColor,
      collapsed: gd.collapsed,
      collapsedX: gd.collapsedX,
      collapsedY: gd.collapsedY,
      memberEntityIds: gd.memberEntityIds.filter(id => allValidIds.has(id)),
      memberNodeIds: memberNodeIds.filter(id => validEntityIds.has(id)),
      memberGroupIds: memberGroupIds.filter(id => groupIdSet.has(id)),
      layerId: ent.layerId,
      displayMode: gd.displayMode ?? 'invisible',
    };
  });

  const allEntityIds: string[] = [];
  const collapsedGroupIds: string[] = [];
  const expandedGroupIds: string[] = [];
  const groupMap = new Map(groups.map(g => [g.id, g]));

  const draggedGroup = groupMap.get(groupId);
  if (draggedGroup?.collapsed) collapsedGroupIds.push(groupId);
  else if (draggedGroup) expandedGroupIds.push(groupId);

  function collectMembers(gId: string) {
    const g = groupMap.get(gId);
    if (!g) return;
    for (const nodeId of g.memberNodeIds) allEntityIds.push(nodeId);
    for (const childId of g.memberGroupIds) {
      const child = groupMap.get(childId);
      if (child?.collapsed) collapsedGroupIds.push(childId);
      else if (child) expandedGroupIds.push(childId);
      collectMembers(childId);
    }
  }
  collectMembers(groupId);

  if (selectedEntityIds.includes(groupId)) {
    for (const otherGroupId of selectedEntityIds) {
      if (otherGroupId !== groupId) {
        const otherGroup = groupMap.get(otherGroupId);
        if (otherGroup?.collapsed) collapsedGroupIds.push(otherGroupId);
        else if (otherGroup) expandedGroupIds.push(otherGroupId);
        collectMembers(otherGroupId);
      }
    }
  }

  return {
    entityIds: [...new Set(allEntityIds)],
    collapsedGroupIds: [...new Set(collapsedGroupIds)],
    expandedGroupIds: [...new Set(expandedGroupIds)],
  };
}
